import {
  alpha,
  Box,
  Chip,
  darken,
  IconButton,
  lighten,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { RowsDataSource, TypeDataSource } from "../../types";
import { ReactComponent as MySql } from "../../../../assets/logosSvg/mySQL_icon.svg";
import { ReactComponent as Postgres } from "../../../../assets/logosSvg/postgresql.svg";
import { ReactComponent as MariaDB } from "../../../../assets/logosSvg/mariaDB_icon.svg";
import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface CardDataSourceProps extends RowsDataSource {
  setIsEditingDataSource?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedDataSource: React.Dispatch<
    React.SetStateAction<RowsDataSource | null>
  >;
}

export default function CardDataSource({
  setIsEditingDataSource,
  setOpenDeleteModal,
  setSelectedDataSource,
  ...cardData
}: CardDataSourceProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const getImageByType = (type: keyof typeof TypeDataSource): JSX.Element => {
    const props = { width: 45, height: 45 };
    switch (type) {
      case "POSTGRES":
        return <Postgres {...props} />;
      case "MYSQL":
        return <MySql {...props} style={{ fill: "#F29111" }} />;
      case "MARIADB":
        return <MariaDB {...props} style={{ fill: "#C0765A" }} />;
      default:
        return <Postgres {...props} />;
    }
  };

  const styles = {
    boxContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      gap: 3,
      border: `1px solid ${isDark ? theme.palette.stroke : "#CACDD0"}`,
      padding: 1.5,
      borderRadius: 1,
      minWidth: 0,
    },
    stackTitles: {
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      minWidth: 0,
    },
    stackSubTitles: {
      display: "flex",
      flexDirection: "row",
      gap: 1,
    },
    subTitle: {
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    stackBtn: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "start",
      alignItems: "center",
    },
    boxStacks: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "start",
      alignItems: "center",
      gap: 2,
      minWidth: 0,
      flex: 1,
    },
  };
  return (
    <>
      <Box sx={styles.boxContainer}>
        <Box sx={styles.boxStacks}>
          <Stack>{getImageByType(cardData.enumType)}</Stack>
          <Stack sx={styles.stackTitles}>
            <Stack sx={styles.stackSubTitles}>
              <Tooltip title={cardData.name}>
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cardData.name}
                </Typography>
              </Tooltip>
              {cardData?.default && (
                <>
                  <Typography sx={styles.subTitle}>|</Typography>
                  <Chip
                    key={`chip-isDefault-${cardData.id}`}
                    label={"Default"}
                    sx={{
                      height: 20,
                      minWidth: 55,
                      alignSelf: "flex-end",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: isDark
                        ? lighten(theme.palette.primary.main, 0.5)
                        : darken(theme.palette.primary.main, 0.4),
                      backgroundColor: isDark
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.4),
                    }}
                  />
                </>
              )}
            </Stack>

            <Stack sx={styles.stackSubTitles}>
              <Typography sx={styles.subTitle}>
                {
                  TypeDataSource[
                    cardData.enumType as keyof typeof TypeDataSource
                  ]
                }
              </Typography>
              <Typography sx={styles.subTitle}>|</Typography>
              <Typography sx={styles.subTitle}>{cardData.host}</Typography>
            </Stack>
          </Stack>
        </Box>
        <Stack sx={styles.stackBtn}>
          <IconButton
            size="small"
            onClick={() => {
              setIsEditingDataSource?.(true);
              navigate(`/configuracoes/datasource/editar/${cardData.id}`);
            }}
          >
            <Edit sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setOpenDeleteModal(true);
              setSelectedDataSource(cardData);
            }}
          >
            <Delete sx={{ color: theme.palette.error.main }} />
          </IconButton>
        </Stack>
      </Box>
    </>
  );
}
